import React, { useState } from 'react';
import { Alert, Button, message, Modal, Progress, Space, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import api from '../services/api';
import { TrainingDocument, TrainingVideo } from '../types';
import { trainingUploadError, validateTrainingFile } from '../utils/trainingUpload';

type Props = {
  material: TrainingVideo | TrainingDocument;
  kind: 'video' | 'presentation' | 'transcript';
  onClose: () => void;
  onReplaced: () => Promise<void>;
};

const ReplaceTrainingFile: React.FC<Props> = ({ material, kind, onClose, onReplaced }) => {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState('');
  const accept = kind === 'video' ? '.mp4,.webm' : kind === 'presentation' ? '.pdf' : '.txt';
  const replace = async () => {
    if (busy) return;
    if (!file) { setError('Выберите новый файл'); return; }
    if (!material.file_revision) { setError('Обновите список материалов и откройте замену файла заново'); return; }
    setBusy(true); setPercent(0); setError('');
    const body = new FormData();
    body.append('file', file);
    body.append('expected_file_revision', material.file_revision);
    try {
      await api.put(`/video/admin/${kind === 'video' ? 'videos' : 'documents'}/${material.id}/file`, body, {
        timeout: 0,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: event => { if (event.total) setPercent(Math.round(event.loaded / event.total * 100)); },
      });
      message.success('Файл заменён. Статистика сохранена');
      await onReplaced();
      onClose();
    } catch (err) {
      setError(trainingUploadError(err, 'Не удалось заменить файл. Проверьте список материалов перед повторной загрузкой.'));
    } finally { setBusy(false); }
  };
  return <Modal className="video-admin-modal" title="Заменить файл" open onCancel={() => !busy && onClose()} onOk={replace}
    okText="Заменить файл" cancelText="Отмена" confirmLoading={busy} cancelButtonProps={{ disabled: busy }} closable={!busy} maskClosable={false} keyboard={!busy}>
    <Space data-guide="replace-file-form" direction="vertical" size={16} style={{ width: '100%' }}>
      <div><Typography.Text strong>{material.title}</Typography.Text><br /><Typography.Text type="secondary">Текущий файл: {material.original_filename}</Typography.Text></div>
      <Alert type="info" showIcon message="Просмотры и отметки о прочтении сохранятся" description="Новый файл займёт место текущего. Название, дата обучения и ссылки на материал останутся прежними." />
      {kind === 'transcript' && <Typography.Text>Текст из нового TXT заменит текущую транскрибацию, включая правки в редакторе.</Typography.Text>}
      <Upload accept={accept} maxCount={1} disabled={busy} beforeUpload={selected => {
        const validation = validateTrainingFile(selected, kind);
        if (validation) { setError(validation); return Upload.LIST_IGNORE; }
        setFile(selected); setError(''); return false;
      }} fileList={file ? [{ uid: '-1', name: file.name, status: 'done' }] : []} onRemove={() => { setFile(null); return true; }}>
        <Button icon={<UploadOutlined />} disabled={busy}>Выбрать новый файл</Button>
      </Upload>
      <Typography.Text type="secondary">{kind === 'video' ? 'MP4 или WebM — до 4 ГБ.' : kind === 'presentation' ? 'PDF — до 100 МБ.' : 'TXT — до 5 МБ.'}</Typography.Text>
      {error && <Alert type="error" showIcon message={error} />}
      {busy && <><Progress percent={percent} status="active" /><Typography.Text type="secondary">{percent >= 100 ? 'Файл передан. Подтверждаем замену…' : 'Передаём новый файл. Дождитесь окончания загрузки.'}</Typography.Text></>}
    </Space>
  </Modal>;
};

export default ReplaceTrainingFile;
